export const SUPPLIER_CATEGORIES = ['assessment', 'quote', 'repair', 'restoration', 'make safe'] as const;

export const SUPPLIER_COUNTRIES = ['AUSTRALIA'] as const;

export type SupplierCountry = (typeof SUPPLIER_COUNTRIES)[number];

export const SUPPLIER_COUNTRY_LABELS = {
    AUSTRALIA: 'Australia',
} as const satisfies Record<SupplierCountry, string>;

export const SUPPLIER_STATES = {
    AUSTRALIA: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
} as const satisfies Record<SupplierCountry, string[]>;

export const SUPPLIER_SERVICING_AREA_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const;

export type SupplierServicingAreaState = (typeof SUPPLIER_SERVICING_AREA_STATES)[number];

/**
 * Lifecycle of the async address-geocoding pipeline (docs/plan/supplier-geolocation/plan.md).
 * `skipped` = non-AU address, no usable address, or geocoder not configured.
 */
export const SUPPLIER_GEOCODE_STATUSES = ['pending', 'ok', 'failed', 'skipped'] as const;

export type SupplierGeocodeStatus = (typeof SUPPLIER_GEOCODE_STATUSES)[number];

export const AFSL_STATUSES = ['holder', 'authorized representative', 'none'] as const;

export type AFSLStatus = (typeof AFSL_STATUSES)[number];

export const AFSL_STATUSES_LABELS = {
    holder: 'Holder',
    // Stored value keeps its original spelling; the label follows British English.
    'authorized representative': 'Authorised representative',
    none: 'None',
} as const satisfies Record<AFSLStatus, string>;

export const SUPPLIER_JOB_STATUSES = ['New', 'Accepted', 'In progress', 'Completed'] as const;

/**
 * Explicit repairer marker (CUR-1102). The `repair` supplier category is an editable org setting
 * that also holds glass fitters and mechanics, so allocation must never infer "repairer" from it.
 */
export const SUPPLIER_VENDOR_TYPES = ['repairer'] as const;

export type SupplierVendorType = (typeof SUPPLIER_VENDOR_TYPES)[number];

export const SUPPLIER_VENDOR_TYPE_LABELS = {
    repairer: 'Repairer',
} as const satisfies Record<SupplierVendorType, string>;

/** Australian Business Register status as verified at load or by an admin (CUR-1102). */
export const SUPPLIER_ABR_STATUSES = ['active', 'cancelled'] as const;

export type SupplierAbrStatus = (typeof SUPPLIER_ABR_STATUSES)[number];

export const SUPPLIER_ABR_STATUS_LABELS = {
    active: 'Active',
    cancelled: 'Cancelled',
} as const satisfies Record<SupplierAbrStatus, string>;

/**
 * Outcome of the last Clear21 identity reconciliation for a repairer (CUR-1107). Written only by
 * the reconciliation, keyed on the Clear21 external code; null = never reconciled.
 */
export const SUPPLIER_CLEAR21_MATCH_STATUSES = [
    'matched',
    'renamed',
    'not_found',
    'duplicate_code',
    'no_code',
] as const;

export type SupplierClear21MatchStatus = (typeof SUPPLIER_CLEAR21_MATCH_STATUSES)[number];

export const SUPPLIER_CLEAR21_MATCH_STATUS_LABELS = {
    matched: 'Matched',
    renamed: 'Renamed in Clear21',
    // Covers a code Clear21 does not list and a listed repairer that carries no repairer id.
    not_found: 'Not identified in Clear21',
    duplicate_code: 'Duplicate code in Clear21',
    no_code: 'No Clear21 code',
} as const satisfies Record<SupplierClear21MatchStatus, string>;

/** Statuses that leave the repairer without a positively identified Clear21 identity. */
export const SUPPLIER_CLEAR21_UNMATCHED_STATUSES = [
    'not_found',
    'duplicate_code',
    'no_code',
] as const satisfies readonly SupplierClear21MatchStatus[];

/**
 * Where a repairer stands in being sent to our Clear21 company (CUR-1658). Written by the sync
 * job and the supplier write service; null = never queued.
 */
export const SUPPLIER_CLEAR21_SYNC_STATUSES = ['pending', 'sent', 'failed'] as const;

export type SupplierClear21SyncStatus = (typeof SUPPLIER_CLEAR21_SYNC_STATUSES)[number];

export const SUPPLIER_CLEAR21_SYNC_STATUS_LABELS = {
    pending: 'Waiting to send',
    sent: 'Sent',
    failed: 'Not sent',
} as const satisfies Record<SupplierClear21SyncStatus, string>;

/** Most repairers one "Send to Clear21" request may name. */
export const MAX_CLEAR21_SYNC_REQUEST_SUPPLIERS = 500;
